import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Download, FileText, Award, BookOpen } from "lucide-react";
import { api, participant } from "../lib/api";
import { Brand } from "../components/Brand";
import { ResultsCharts } from "../components/ResultsCharts";
import { Certificate } from "../components/Certificate";
import { WorkbookQuestion } from "../components/WorkbookQuestion";
import { downloadUserCsv, printReport } from "../lib/report";

export default function Results() {
  const nav = useNavigate();
  const [report, setReport] = useState(null);
  const [cert, setCert] = useState(false);
  const [askWorkbook, setAskWorkbook] = useState(false);
  const p = participant.get();

  useEffect(() => {
    if (!p) {
      nav("/");
      return;
    }
    api.get(`/participant/${p.code}/results`).then(({ data }) => setReport(data));
    // eslint-disable-next-line
  }, []);

  return (
    <div className="min-h-screen bg-[#F1F5F9]">
      <header className="no-print sticky top-0 z-10 border-b border-[#E2E8F0] bg-[#F1F5F9]/95 px-6 py-4 backdrop-blur sm:px-10">
        <div className="mx-auto flex max-w-5xl items-center justify-between">
          <Brand subtitle="Results" />
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => nav("/activities")} data-testid="book-of-activities-btn"
              className="flex items-center gap-1.5 rounded-full bg-[#2563EB] px-4 py-2 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5">
              <BookOpen className="h-4 w-4" strokeWidth={1.5} /> Book of Activities
            </button>
            {report && (
              <>
                <button onClick={() => downloadUserCsv(report)} data-testid="download-csv-btn"
                  className="flex items-center gap-1.5 rounded-full border border-[#1E293B]/20 px-4 py-2 text-sm text-[#1E293B] transition-transform duration-200 hover:-translate-y-0.5">
                  <FileText className="h-4 w-4" strokeWidth={1.5} /> CSV
                </button>
                <button onClick={printReport} data-testid="download-pdf-btn"
                  className="flex items-center gap-1.5 rounded-full bg-[#2563EB] px-4 py-2 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5">
                  <Download className="h-4 w-4" strokeWidth={1.5} /> PDF
                </button>
                <button onClick={() => setAskWorkbook(true)} data-testid="certificate-btn"
                  className="flex items-center gap-1.5 rounded-full bg-[#1E293B] px-4 py-2 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5">
                  <Award className="h-4 w-4" strokeWidth={1.5} /> Certificate
                </button>
              </>
            )}
            <button onClick={() => nav("/dashboard")} data-testid="back-dashboard-btn"
              className="flex items-center gap-1.5 text-sm text-[#1E293B]/70 transition-colors hover:text-[#2563EB]">
              <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Dashboard
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-10 sm:px-10" data-report>
        <div className="flex items-center gap-4">
          <img src="/gcrc-logo.png" alt="GCRC seal" className="h-16 w-16 object-contain" />
          {report?.logo && <img src={report.logo} alt="Program logo" className="max-h-14 object-contain" />}
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#2563EB]">Study Abroad Global Experience Certification · {report?.code}</div>
            <h1 className="mt-2 font-display text-4xl text-[#1E293B]">{report?.name || "Your"} Results</h1>
          </div>
        </div>
        <p className="mt-2 text-[#1E293B]/60">Weighted development score combining all nine competencies. Complete both phases to see your full growth.</p>
        <div className="mt-8">
          {report ? <ResultsCharts report={report} /> : <div className="text-[#1E293B]/60">Loading…</div>}
        </div>
      </main>

      {cert && report && <Certificate report={report} onClose={() => setCert(false)} />}
      {askWorkbook && report && (
        <WorkbookQuestion
          code={report.code}
          onClose={() => setAskWorkbook(false)}
          onDone={(answer) => { setReport({ ...report, workbook_usage: answer }); setAskWorkbook(false); setCert(true); }}
        />
      )}
    </div>
  );
}
